/**
 * Trip Atlas query language — the search bar over the 3D trip view.
 *
 * Deterministic, like the Rack Explorer's query bar: every hit is a trip row,
 * and `intent` says how the text was read, so a miss can be told from a
 * misparse. Words combine with AND; each word is read as the first thing it
 * matches:
 *
 *   help | ?                              what you can type
 *   PRJ-2026-0176 | 0176                  one trip by project number
 *   RUN-2026-001 | run | runs             trips on runs (or one run)
 *   2025 | 2025-03 | mar | mar 2025       a year, a month, a month name
 *   q3 | q3 2025                          a quarter (by visit start), any year or one
 *   long                                  4 days or more on site
 *   unpriced | nocost                     no deal amount / no cost recorded in Zoho
 *   invoiced | settled | history | unreconciled | ready | review | overdue
 *     | planned | scheduled | cancelled   a trip state
 *   open | open items                     properties with open items
 *   hcc | rancho | luma | "stanford hq"   a property (name, customer, or code alias)
 *   anything else                         text in the scope, deal names, PO,
 *                                         lead technician or invoice number
 */
import { LONG_TRIP_DAYS, TRIP_STATES, tripDays, type AtlasTripInput, type TripAtlas, type TripState } from './tripAtlas';

export interface TripQueryResult {
  intent: string;
  /** Matching trip ids; null = no query (everything shown). */
  tripIds: Set<string> | null;
  /** A single trip the query pinned (project number). */
  focusTripId?: string;
}

export const TRIP_QUERY_EXAMPLES = ['HCC 2025', 'long', 'q3 2025', 'runs', 'MRC mar 2025', 'PRJ-2026-0176', 'kickoff', 'unpriced'];

/** One-click filter words shown as toggles beside the search. */
export const QUICK_FILTERS: { word: string; label: string }[] = [
  { word: 'long', label: `${LONG_TRIP_DAYS}+ days` },
  { word: 'runs', label: 'Runs' },
  { word: 'open', label: 'Open items' },
  { word: 'review', label: 'Needs review' },
  { word: 'unpriced', label: 'No deal amount' },
];

const STATE_WORDS: Record<string, TripState> = {
  invoiced: 'settled', settled: 'settled', billed: 'settled',
  history: 'history', unreconciled: 'history', imported: 'history',
  ready: 'ready', unbilled: 'ready',
  review: 'review', overdue: 'review',
  planned: 'planned', scheduled: 'planned', upcoming: 'planned',
  cancelled: 'cancelled', canceled: 'cancelled',
};

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * Short names crews use for the Stanford properties, mapped to words that
 * appear in the location names. Anything not here still matches by name.
 */
export const PROPERTY_ALIASES: Record<string, string[]> = {
  hcc: ['charlotte uptown'], hclt: ['charlotte uptown'],
  mrc: ['rancho cordova'],
  lsf: ['luma hotel san francisco'], 'luma sf': ['luma hotel san francisco'],
  lny: ['luma hotel times square'], 'luma nyc': ['luma hotel times square'],
  hdc: ['national mall'],
  hwd: ['dulles'], iadah: ['dulles'],
  dpx: ['phoenix'], dsp: ['phoenix'], dtsp: ['phoenix'],
  saq: ['albuquerque'],
  hwh: ['woodland hills'], hwla: ['woodland hills'],
  hsc: ['santa clara'],
  hbg: ['airport bayfront'],
  shc: ['stanford hotels corporation'], 'stanford hq': ['stanford hotels corporation'],
};

export interface TripSearchIndex {
  /** Free text per trip: scope, deal names, PO, lead, invoice. */
  text: Map<string, string>;
  runNumbers: Map<string, string>;
}

type Predicate = { label: string; test: (t: AtlasTripInput) => boolean };

function includesAny(hay: string, needles: string[]) {
  return needles.some((n) => hay.includes(n));
}

export function runTripQuery(atlas: TripAtlas, index: TripSearchIndex, raw: string): TripQueryResult {
  const q = raw.trim().toLowerCase();
  if (!q) return { intent: '', tripIds: null };
  if (q === 'help' || q === '?') {
    return { intent: 'Try: a property (HCC, MRC, LUMA), a year, quarter or month (2025, q3 2025, mar 2025), long (4+ days), unpriced, nocost, a state (history, ready, review, invoiced), runs, open items, a PRJ number, or any word from a deal name.', tripIds: null };
  }

  const trips = [...atlas.byId.values()];
  const blockOf = atlas.blockById;

  // A whole property name (what clicking a property in the list sends).
  const lane = atlas.lanes.find((l) => l.name.toLowerCase() === q);
  if (lane) {
    const ids = new Set(trips.filter((t) => t.location_id === lane.locationId).map((t) => t.trip_id));
    return { intent: `property “${lane.name}” — ${ids.size} trip${ids.size === 1 ? '' : 's'}`, tripIds: ids };
  }
  const openLocations = new Set(atlas.lanes.filter((l) => l.openItems > 0).map((l) => l.locationId));
  const preds: Predicate[] = [];
  let focusTripId: string | undefined;

  // Two-word phrases first (aliases like "luma sf", "open items", "mar 2025").
  const words = q.split(/\s+/).filter(Boolean);
  for (let i = 0; i < words.length; i += 1) {
    const w = words[i];
    const pair = i + 1 < words.length ? `${w} ${words[i + 1]}` : null;

    if (pair && PROPERTY_ALIASES[pair]) {
      const names = PROPERTY_ALIASES[pair];
      preds.push({ label: `property “${pair.toUpperCase()}”`, test: (t) => includesAny((t.location_name ?? '').toLowerCase(), names) });
      i += 1;
      continue;
    }
    if (pair && (pair === 'open items' || pair === 'open item')) {
      preds.push({ label: 'properties with open items', test: (t) => openLocations.has(t.location_id) });
      i += 1;
      continue;
    }
    if (pair && /^q[1-4]$/.test(w) && /^(19|20)\d\d$/.test(words[i + 1])) {
      const qn = Number(w[1]);
      const y = words[i + 1];
      preds.push({ label: `Q${qn} ${y}`, test: (t) => startQuarter(t) === `${y}-${qn}` });
      i += 1;
      continue;
    }
    if (pair && MONTHS[w] && /^\d{4}$/.test(words[i + 1])) {
      const ym = `${words[i + 1]}-${String(MONTHS[w]).padStart(2, '0')}`;
      preds.push({ label: `in ${ym}`, test: (t) => onsiteIn(t, ym) });
      i += 1;
      continue;
    }

    // PRJ-2026-0176, or its last four digits (0176). A bare year is a year, not a trip.
    if (/^prj-\d{4}-\d{4}$/.test(w) || (/^\d{4}$/.test(w) && !/^(19|20)\d\d$/.test(w))) {
      const hit = trips.find((t) => t.project_number.toLowerCase() === w || t.project_number.endsWith(`-${w}`));
      preds.push({ label: `trip ${hit?.project_number ?? w.toUpperCase()}`, test: (t) => t.trip_id === hit?.trip_id });
      if (hit) focusTripId = hit.trip_id;
      continue;
    }
    if (/^run-\d{4}-\d+$/.test(w)) {
      preds.push({ label: `run ${w.toUpperCase()}`, test: (t) => index.runNumbers.get(t.trip_id)?.toLowerCase() === w });
      continue;
    }
    if (w === 'run' || w === 'runs') {
      preds.push({ label: 'on a run', test: (t) => Boolean(blockOf.get(t.trip_id)?.runId) });
      continue;
    }
    if (/^(19|20)\d\d$/.test(w)) {
      preds.push({ label: `in ${w}`, test: (t) => onsiteIn(t, w) });
      continue;
    }
    if (/^(19|20)\d\d-(0[1-9]|1[0-2])$/.test(w)) {
      preds.push({ label: `in ${w}`, test: (t) => onsiteIn(t, w) });
      continue;
    }
    if (MONTHS[w]) {
      const mm = String(MONTHS[w]).padStart(2, '0');
      preds.push({ label: `in ${w[0].toUpperCase()}${w.slice(1)} (any year)`, test: (t) => monthsOnsite(t).some((ym) => ym.endsWith(`-${mm}`)) });
      continue;
    }
    if (/^q[1-4]$/.test(w)) {
      const qn = w[1];
      preds.push({ label: `Q${qn} (any year)`, test: (t) => startQuarter(t)?.endsWith(`-${qn}`) ?? false });
      continue;
    }
    if (w === 'long' || w === 'multiday' || w === '4d+') {
      preds.push({ label: `${LONG_TRIP_DAYS}+ days on site`, test: (t) => tripDays(t) >= LONG_TRIP_DAYS });
      continue;
    }
    if (w === 'unpriced') {
      preds.push({ label: 'no deal amount', test: (t) => !t.finance || t.finance.revenue === null });
      continue;
    }
    if (w === 'nocost') {
      preds.push({ label: 'no cost in Zoho', test: (t) => !t.finance || t.finance.cost === null });
      continue;
    }
    if (STATE_WORDS[w]) {
      const s = STATE_WORDS[w];
      preds.push({ label: TRIP_STATES[s].label.toLowerCase(), test: (t) => blockOf.get(t.trip_id)?.state === s });
      continue;
    }
    if (w === 'open') {
      preds.push({ label: 'properties with open items', test: (t) => openLocations.has(t.location_id) });
      continue;
    }
    if (PROPERTY_ALIASES[w]) {
      const names = PROPERTY_ALIASES[w];
      preds.push({ label: `property “${w.toUpperCase()}”`, test: (t) => includesAny((t.location_name ?? '').toLowerCase(), names) });
      continue;
    }
    const propertyHit = trips.some((t) => `${t.location_name ?? ''} ${t.customer_name ?? ''}`.toLowerCase().includes(w));
    if (propertyHit && w.length >= 3) {
      preds.push({
        label: `property or text “${w}”`,
        test: (t) => `${t.location_name ?? ''} ${t.customer_name ?? ''} ${index.text.get(t.trip_id) ?? ''}`.toLowerCase().includes(w),
      });
      continue;
    }
    preds.push({ label: `text “${w}”`, test: (t) => (index.text.get(t.trip_id) ?? '').includes(w) });
  }

  const ids = new Set(trips.filter((t) => preds.every((p) => p.test(t))).map((t) => t.trip_id));
  const n = ids.size;
  return {
    intent: `${preds.map((p) => p.label).join(' · ')} — ${n} trip${n === 1 ? '' : 's'}`,
    tripIds: ids,
    focusTripId: focusTripId && ids.has(focusTripId) ? focusTripId : undefined,
  };
}

/** "2025-3" for a visit starting in Q3 2025 (the quarter a trip counts in). */
function startQuarter(t: Pick<AtlasTripInput, 'start_date'>): string | null {
  if (!t.start_date) return null;
  return `${t.start_date.slice(0, 4)}-${Math.ceil(Number(t.start_date.slice(5, 7)) / 3)}`;
}

/** Every YYYY-MM a trip was on site in. */
function monthsOnsite(t: Pick<AtlasTripInput, 'start_date' | 'end_date'>): string[] {
  if (!t.start_date) return [];
  const out: string[] = [];
  const [sy, sm] = t.start_date.slice(0, 7).split('-').map(Number);
  const [ey, em] = (t.end_date ?? t.start_date).slice(0, 7).split('-').map(Number);
  for (let y = sy, m = sm; y < ey || (y === ey && m <= em); m === 12 ? ((y += 1), (m = 1)) : (m += 1)) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
  }
  return out;
}

function onsiteIn(t: Pick<AtlasTripInput, 'start_date' | 'end_date'>, period: string): boolean {
  return monthsOnsite(t).some((ym) => ym.startsWith(period));
}

export function buildSearchIndex(
  trips: AtlasTripInput[],
  dealLabelsByTrip: Map<string, string[]>,
  runNumberByTrip: Map<string, string>,
): TripSearchIndex {
  const text = new Map<string, string>();
  for (const t of trips) {
    text.set(
      t.trip_id,
      [t.scope, t.po_number, t.lead_technician_name, t.invoice_reference, t.project_number, ...(dealLabelsByTrip.get(t.trip_id) ?? [])]
        .filter(Boolean)
        .join(' · ')
        .toLowerCase(),
    );
  }
  return { text, runNumbers: runNumberByTrip };
}

/** The query word each trip state answers to (legend clicks write these). */
export const STATE_QUERY_WORD: Record<TripState, string> = {
  settled: 'invoiced',
  history: 'history',
  ready: 'ready',
  review: 'review',
  planned: 'planned',
  cancelled: 'cancelled',
};

/**
 * Add a filter word to the query, or take it out if it is already there
 * (legend rows and year chips toggle). Case- and spacing-insensitive; the rest
 * of the query is kept in order.
 */
export function toggleQueryWord(query: string, word: string): string {
  const w = word.toLowerCase();
  const words = query.trim().split(/\s+/).filter(Boolean);
  const has = words.some((x) => x.toLowerCase() === w);
  return (has ? words.filter((x) => x.toLowerCase() !== w) : [...words, word]).join(' ');
}

/** Whether a filter word is currently in the query. */
export function queryHasWord(query: string, word: string): boolean {
  const w = word.toLowerCase();
  return query.trim().split(/\s+/).some((x) => x.toLowerCase() === w);
}
